import { AccountLayout } from '@/components/AccountLayout';
import { usePriceAlerts, useDeletePriceAlert, useUpdatePriceAlert } from '@/hooks/usePriceAlerts';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Link } from 'react-router-dom';
import { Bell, Trash2, ExternalLink, TrendingDown, TrendingUp, AlertCircle } from 'lucide-react';
import { optimizeImageUrl } from '@/lib/imageUtils';
import { formatDistanceToNow } from 'date-fns';

export default function PriceAlerts() {
  const { data: alerts, isLoading } = usePriceAlerts();
  const deleteAlert = useDeletePriceAlert();
  const updateAlert = useUpdatePriceAlert();

  const handleToggleActive = (alertId: string, isActive: boolean) => {
    updateAlert.mutate({ alertId, is_active: !isActive });
  };

  const handleDelete = (alertId: string) => {
    deleteAlert.mutate(alertId);
  };

  if (isLoading) {
    return (
      <AccountLayout>
        <div className="space-y-6">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </AccountLayout>
    );
  }

  const activeAlerts = alerts?.filter(a => a.is_active) || [];
  const inactiveAlerts = alerts?.filter(a => !a.is_active) || [];
  const isEmpty = !alerts || alerts.length === 0;

  return (
    <AccountLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-foreground">Price Alerts</h1>
          <p className="text-muted-foreground">
            Get notified when products hit your target price
          </p>
        </div>

        {/* Active Alerts Summary */}
        {!isEmpty && (
          <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Active Price Alerts</p>
                  <p className="text-3xl font-bold text-foreground">
                    {activeAlerts.length}
                  </p>
                </div>
                <Bell className="w-12 h-12 text-primary/30" />
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                We'll email you when any of these products meet your price criteria
              </p>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {isEmpty && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Bell className="w-16 h-16 text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">
                No price alerts yet
              </h3>
              <p className="text-muted-foreground text-center max-w-md mb-6">
                Set price alerts on products you're interested in and we'll notify you when they drop to your target price
              </p>
              <Link to="/browse">
                <Button>Browse Products</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Active Alerts */}
        {activeAlerts.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">Active Alerts</h2>
            <div className="space-y-3">
              {activeAlerts.map((alert) => (
                <PriceAlertCard
                  key={alert.id}
                  alert={alert}
                  onToggleActive={handleToggleActive}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </div>
        )}

        {/* Inactive Alerts */}
        {inactiveAlerts.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">Inactive Alerts</h2>
            <div className="space-y-3">
              {inactiveAlerts.map((alert) => (
                <PriceAlertCard
                  key={alert.id}
                  alert={alert}
                  onToggleActive={handleToggleActive}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </AccountLayout>
  );
}

function PriceAlertCard({ alert, onToggleActive, onDelete }: any) {
  const product = alert.product;
  if (!product) return null;

  const currentPrice = product.current_price || product.price || 0;
  const targetPrice = alert.target_price;
  const priceMatch = currentPrice <= targetPrice;
  const priceDiff = currentPrice - targetPrice;
  const diffPercent = targetPrice > 0 ? ((priceDiff / targetPrice) * 100).toFixed(0) : 0;

  const getAlertTypeLabel = (type: string) => {
    switch (type) {
      case 'below':
        return 'Below Target';
      case 'any_change':
        return 'Any Change';
      case 'sale':
        return 'On Sale';
      default:
        return type;
    }
  };

  const getAlertStatus = () => {
    if (!alert.is_active) {
      return { label: 'Inactive', color: 'text-muted-foreground', icon: AlertCircle };
    }
    if (priceMatch && alert.alert_type === 'below') {
      return { label: 'Price Met!', color: 'text-green-600', icon: TrendingDown };
    }
    return { label: 'Watching', color: 'text-primary', icon: Bell };
  };

  const status = getAlertStatus();
  const StatusIcon = status.icon;

  return (
    <Card className={!alert.is_active ? 'opacity-60' : ''}>
      <CardContent className="flex items-center gap-4 p-4">
        {/* Product Image */}
        <Link to={`/product/${product.slug}`} className="shrink-0">
          <div className="w-16 h-16 rounded-md overflow-hidden bg-muted">
            {product.image ? (
              <img
                src={optimizeImageUrl(product.image, { width: 64, height: 64 })}
                alt={product.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span className="text-2xl">📦</span>
              </div>
            )}
          </div>
        </Link>

        {/* Product Info */}
        <div className="flex-1 min-w-0 space-y-2">
          <Link to={`/product/${product.slug}`}>
            <h3 className="font-semibold text-foreground hover:text-primary transition-colors line-clamp-1">
              {product.title}
            </h3>
          </Link>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Current:</span>
              <span className="font-semibold text-foreground">${currentPrice}</span>
            </div>
            {alert.alert_type === 'below' && (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Target:</span>
                  <span className="font-semibold text-foreground">${targetPrice}</span>
                </div>
                {priceDiff > 0 ? (
                  <Badge variant="outline" className="text-xs">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    {diffPercent}% above
                  </Badge>
                ) : (
                  <Badge variant="default" className="text-xs bg-green-600">
                    <TrendingDown className="w-3 h-3 mr-1" />
                    Target met!
                  </Badge>
                )}
              </>
            )}
          </div>

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <StatusIcon className={`w-3 h-3 ${status.color}`} />
            <span className={status.color}>{status.label}</span>
            <span>•</span>
            <span>{getAlertTypeLabel(alert.alert_type)}</span>
            <span>•</span>
            <span>Created {formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <Switch
              checked={alert.is_active}
              onCheckedChange={() => onToggleActive(alert.id, alert.is_active)}
            />
          </div>

          <Link to={`/product/${product.slug}`}>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
              <ExternalLink className="w-4 h-4" />
            </Button>
          </Link>

          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
            onClick={() => onDelete(alert.id)}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
